import { useState, useEffect } from "react";
import { nhost } from "@/lib/nhost";

export type UserRole = "guest" | "individual" | "reseller" | "admin";

interface AuthUser {
  id: string;
  name: string;
  email: string;
  phone?: string;
  role: UserRole;
  adminRole?: "super_admin" | "support_admin" | null;
  cashbackBalance?: number;
  avatarUrl?: string | null;
  resellerId?: string | null;
}

const MAP_USER_QUERY = `
  query MapUser($id: uuid!) {
    my_admin_status(where: { user_id: { _eq: $id } }) {
      admin_id
      admin_role
    }
    resellers(where: { user_id: { _eq: $id } }) {
      id
      total_earned
      pending_balance
      avatar_url
    }
    profiles(where: { id: { _eq: $id } }) {
      cashback_balance
      avatar_url
      account_type
    }
  }
`;

export const useAuth = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  const mapUser = async (u: any): Promise<AuthUser> => {
    const base: AuthUser = {
      id: u.id,
      name: u.displayName || u.email?.split("@")[0] || "User",
      email: u.email || "",
      phone: u.phoneNumber || "",
      role: "individual",
      cashbackBalance: 0,
      avatarUrl: null,
    };

    try {
      const { body } = await nhost.graphql.request({
        query: MAP_USER_QUERY,
        variables: { id: u.id },
      });

      const adminRecord = body.data?.my_admin_status?.[0];
      if (adminRecord) {
        base.role = "admin";
        base.adminRole = adminRecord.admin_role;
        return base;
      }

      const resellerRecord = body.data?.resellers?.[0];
      const profileRecord = body.data?.profiles?.[0];
      const accountType = profileRecord?.account_type || "customer";

      if (accountType === "reseller" && resellerRecord) {
        base.role = "reseller";
        base.avatarUrl = resellerRecord.avatar_url || profileRecord?.avatar_url || null;
        base.resellerId = resellerRecord.id || null;
        base.cashbackBalance = Number(profileRecord?.cashback_balance) || 0;
        return base;
      }

      if (profileRecord) {
        base.cashbackBalance = Number(profileRecord.cashback_balance) || 0;
        base.avatarUrl = profileRecord.avatar_url || null;
      }
    } catch (err) {
      console.error("mapUser error:", err);
    }

    return base;
  };

  useEffect(() => {
    let mounted = true;

    const init = async () => {
      try {
                const session = nhost.sessionStorage.get();
        if (session?.user && mounted) {
          const mapped = await mapUser(session.user);
          setUser(mapped);
          setIsLoggedIn(true);
        }
      } catch (err) {
        console.error("Auth init error:", err);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    init();

        const unsubscribe = nhost.sessionStorage.onChange(async (session) => {
      if (!mounted) return;
      if (session?.user) {
        try {
          const mapped = await mapUser(session.user);
          setUser(mapped);
          setIsLoggedIn(true);
        } catch (err) {
          console.error("Auth state change error:", err);
        }
      } else {
        setUser(null);
        setIsLoggedIn(false);
      }
    });

    return () => {
      mounted = false;
      if (typeof unsubscribe === "function") unsubscribe();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logout = async () => {
    await nhost.auth.signOut();
    setUser(null);
    setIsLoggedIn(false);
  };

  const refreshUser = async () => {
    try {
            const session = nhost.sessionStorage.get();
      if (session?.user) {
        const mapped = await mapUser(session.user);
        setUser(mapped);
        setIsLoggedIn(true);
      }
    } catch (err) {
      console.error("Refresh user error:", err);
    }
  };

  return { isLoggedIn, user, logout, loading, refreshUser };
};